import React from "react";
import { format } from "date-fns";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  TooltipContentProps,
  XAxis,
  YAxis,
} from "recharts";
import { TickProps, propsAreTickProps } from "@sscs/types";
import { CircleIcon as MUICircleIcon } from "@sscs/ui/icons";
import { bytesToGB, compareValues } from "@sscs/utils";
import variables from "Assets/style/variablesExport.module.scss";
import { HistoricalUsage as UsageType } from "Types";
import styles from "./historicalUsageChart.module.scss";

type DataItem = {
  month: string;
  capacity: number;
  flex: number;
  capacityAndFlex: number;
  usedPortal: number;
  usedCli: number;
  usedTotal: number;
};

const transformData = (history: UsageType[]): DataItem[] => {
  const sortedHistory = [...history].sort((first, second) => {
    if (first.year === second.year)
      return compareValues(first.month, second.month, false);
    else return compareValues(first.year, second.year, false);
  });

  return sortedHistory.slice(-12).map((item) => ({
    month: format(new Date(item.year, item.month - 1), "LLL, yyyy"),
    capacity: bytesToGB(item.purchased_processing_bytes),
    flex: bytesToGB(item.flex_bytes),
    capacityAndFlex:
      bytesToGB(item.purchased_processing_bytes) + bytesToGB(item.flex_bytes),
    usedPortal: bytesToGB(item.processing_used_bytes_portal ?? 0),
    usedCli: bytesToGB(item.processing_used_bytes_cli ?? 0),
    usedTotal:
      bytesToGB(item.processing_used_bytes_portal ?? 0) +
      bytesToGB(item.processing_used_bytes_cli ?? 0),
  }));
};

const CustomTooltip = ({
  active,
  payload,
  label,
}: TooltipContentProps<number, string>): React.JSX.Element | null => {
  if (active && payload && payload.length) {
    return (
      <div className={styles.tooltip}>
        <div>{label}</div>
        {payload.map((item) => {
          const value =
            item.dataKey === "capacityAndFlex" ? item.payload.flex : item.value;
          if (typeof value !== "number") {
            throw new Error(`Invalid usage chart tooltip value: ${value}`);
          }

          return (
            <p key={item.name} className={styles["tooltip-item"]}>
              {item.name}:{" "}
              {value && (
                <span className={styles["tooltip-value"]}>
                  {value.toFixed(2)} GB
                </span>
              )}
            </p>
          );
        })}
      </div>
    );
  }

  return null;
};

type LegendItem = {
  id: keyof Omit<DataItem, "month" | "flex" | "usedCli">;
  value: string;
  type: "line" | "circle";
  colorClassName: string;
};

const LEGEND_ITEMS: LegendItem[] = [
  {
    id: "capacity",
    value: "Analysis capacity",
    type: "line",
    colorClassName: styles["capacity"],
  },
  {
    id: "capacityAndFlex",
    value: "Flex",
    type: "line",
    colorClassName: styles["flex"],
  },
  {
    id: "usedPortal",
    value: "Portal usage",
    type: "circle",
    colorClassName: styles["portal-usage"],
  },
  {
    id: "usedTotal",
    value: "Total monthly usage (Portal+CLI)",
    type: "circle",
    colorClassName: styles["total-usage"],
  },
];

const CustomLegend = (): React.JSX.Element => (
  <div className={styles["legend"]}>
    {LEGEND_ITEMS.map(({ id, value, type, colorClassName }) => (
      <div key={id} className={styles["legend-item"]}>
        {type === "line" ? (
          <div className={`${styles["line-icon"]} ${colorClassName}`} />
        ) : (
          <span className={colorClassName}>
            <MUICircleIcon className={styles["circle-icon"]} />
          </span>
        )}
        <div>{value}</div>
      </div>
    ))}
  </div>
);

const CustomizedTick = (props: TickProps) => {
  const { x, y, payload } = props;
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={14}>
        <tspan className={styles["tick-month"]} textAnchor="middle" x="0">
          {payload.value.split(",")[0]}
        </tspan>
        <tspan
          className={styles["tick-year"]}
          textAnchor="middle"
          x="0"
          dy="13"
          dx="-1"
        >
          {payload.value.split(",")[1]}
        </tspan>
      </text>
    </g>
  );
};

type HistoricalUsageProps = {
  history: UsageType[];
};

const HistoricalUsageChart = ({
  history,
}: HistoricalUsageProps): React.JSX.Element => {
  return (
    <ResponsiveContainer width="99%">
      <ComposedChart
        width={600}
        height={150}
        data={transformData(history)}
        margin={{
          top: 10,
          right: 20,
          bottom: 14,
          left: 0,
        }}
        barCategoryGap={0}
      >
        <XAxis
          dataKey="month"
          tick={(props) => {
            if (propsAreTickProps(props)) {
              return <CustomizedTick {...props} />;
            } else {
              console.error("Invalid rechart tick props"); //eslint-disable-line no-console
              return <></>;
            }
          }}
          minTickGap={-35}
        />
        <YAxis
          tickFormatter={(value) => `${value} GB`}
          tick={{ fontSize: "12px" }}
          width={80}
        />
        <Tooltip content={CustomTooltip} />
        <Legend content={<CustomLegend />} wrapperStyle={{ bottom: -3 }} />
        <CartesianGrid stroke={variables.grid_lines_darker_color} />
        <Area
          name="Total monthly usage (Portal+CLI)"
          type="linear"
          dataKey="usedTotal"
          stroke="none"
          fill={variables.used_total_fill_color}
          dot={{
            stroke: variables.used_total_fill_color,
            strokeWidth: 1,
            r: 3, // eslint-disable-line id-length
            fill: "white",
          }}
        />
        <Area
          name="Portal usage"
          type="linear"
          dataKey="usedPortal"
          stroke="none"
          fill={variables.used_portal_fill_color}
          dot={{
            stroke: variables.used_portal_fill_color,
            strokeWidth: 1,
            r: 3, // eslint-disable-line id-length
            fill: "white",
          }}
        />
        <Line
          name="Flex"
          dataKey="capacityAndFlex"
          stroke={variables.flex_fill_color}
          strokeWidth={2}
          dot={false}
          type="stepAfter"
        />
        <Line
          name="Analysis capacity"
          dataKey="capacity"
          stroke={variables.capacity_fill_color}
          strokeWidth={2}
          dot={false}
          type="stepAfter"
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

export default HistoricalUsageChart;
